import React, { useState, useEffect, useRef } from 'react';
import { JwtTokenService } from '../../lib/services/jwtTokenService';
import { ClipboardUtils } from '../../lib/utils/clipboardUtils';
import { ErrorHandler } from '../../lib/utils/errorHandler';
import { EncoderToolType, JwtAlgorithm } from '../../lib/types';
import './JwtTokenEncoder.css';

// Supported algorithms for the service
type SupportedAlgorithm = 'HS256' | 'HS384' | 'HS512' | 'RS256' | 'RS384' | 'RS512';

// Helper to filter algorithm to supported types
const toSupportedAlgorithm = (alg: JwtAlgorithm): SupportedAlgorithm | undefined => {
  const supported: SupportedAlgorithm[] = ['HS256', 'HS384', 'HS512', 'RS256', 'RS384', 'RS512'];
  return supported.includes(alg as SupportedAlgorithm) ? (alg as SupportedAlgorithm) : undefined;
};

// Helper function to detect if input is a public key or private key
const detectKeyType = (keyInput: string): { publicKey?: string; privateKey?: string } => {
  const trimmedKey = keyInput.trim();

  // Check for public key patterns
  const publicKeyPatterns = [
    /-----BEGIN PUBLIC KEY-----/,
    /-----BEGIN RSA PUBLIC KEY-----/,
    // SSH public key format
    /^ssh-rsa\s+AAAAB3NzaC1yc2E/,
    // Raw base64-like public keys (usually start with specific indicators)
    /^(MIIB|MIIC|MIID)/
  ];

  // Check for private key patterns
  const privateKeyPatterns = [
    /-----BEGIN PRIVATE KEY-----/,
    /-----BEGIN RSA PRIVATE KEY-----/,
    /-----BEGIN OPENSSH PRIVATE KEY-----/,
    // PKCS#8 encrypted private key
    /-----BEGIN ENCRYPTED PRIVATE KEY-----/
  ];

  const isPublicKey = publicKeyPatterns.some(pattern => pattern.test(trimmedKey));
  const isPrivateKey = privateKeyPatterns.some(pattern => pattern.test(trimmedKey));

  if (isPublicKey && !isPrivateKey) {
    return { publicKey: trimmedKey };
  } else if (isPrivateKey && !isPublicKey) {
    return { privateKey: trimmedKey };
  } else {
    // If unclear or it's a simple secret, treat as private key for backward compatibility
    return { privateKey: trimmedKey };
  }
};

export const JwtTokenEncoder: React.FC = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState<'decode' | 'encode' | 'verify'>('decode');
  const [algorithm, setAlgorithm] = useState<JwtAlgorithm>(JwtAlgorithm.HS256);
  const [secret, setSecret] = useState('');
  const [payload, setPayload] = useState('');
  const [header, setHeader] = useState('');
  const [expiresIn, setExpiresIn] = useState('1h');
  const [issuer, setIssuer] = useState('');
  const [audience, setAudience] = useState('');
  const [copyFeedback, setCopyFeedback] = useState<'idle' | 'copied' | 'error'>('idle');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSecretWarning, setShowSecretWarning] = useState(false);
  const copyButtonRef = useRef<HTMLButtonElement>(null);
  const processingRef = useRef<string>('');

  useEffect(() => {
    setPayload(prevPayload => {
      try {
        const payloadObj = JSON.parse(prevPayload);
        const updatedPayload = { ...payloadObj };

        if (issuer) {
          updatedPayload.iss = issuer;
        } else {
          delete updatedPayload.iss;
        }

        if (audience) {
          updatedPayload.aud = audience;
        } else {
          delete updatedPayload.aud;
        }

        return JSON.stringify(updatedPayload, null, 2);
      } catch (e) {
        const newPayload: any = {};
        if (issuer) newPayload.iss = issuer;
        if (audience) newPayload.aud = audience;
        return JSON.stringify(newPayload, null, 2);
      }
    });
  }, [issuer, audience]);
  const handleProcess = async () => {
    if (!input.trim() && mode !== 'encode') {
      setOutput('');
      setError(null);
      setHeader('');
      setPayload('');
      return;
    }

    if (mode === 'encode' && (!payload.trim() || !secret.trim())) {
      setError('Payload and secret are required for token generation');
      return;
    }

    setIsLoading(true);
    setError(null);

    // Only clear header/payload if this is a new input, not just algorithm change
    const processingKey = `${mode}-${algorithm}-${input.substring(0, 50)}`;
    const isAlgorithmChangeOnly = processingRef.current &&
      processingRef.current.includes(`${mode}-`) &&
      processingRef.current.endsWith(`-${input.substring(0, 50)}`);

    if (!isAlgorithmChangeOnly) {
      setHeader('');
      // Only clear payload for decode/verify modes, not for encode mode
      if (mode !== 'encode') {
        setPayload('');
      }
    }

    processingRef.current = processingKey;

    try {
      let result;

      if (mode === 'decode') {
        const decodeConfig = JwtTokenService.createConfiguration('decode', {
          token: input,
          algorithm: toSupportedAlgorithm(algorithm)
        });
        result = await JwtTokenService.decode(decodeConfig);

        if (result.status === 'success') {
          // Only set header and payload for display, no need for main output in decode mode
          if (result.metadata) {
            const headerStr = JSON.stringify(result.metadata.header, null, 2);
            const payloadStr = JSON.stringify(result.metadata.payload, null, 2);
            setHeader(headerStr);
            setPayload(payloadStr);
          }
        } else {
          setError(result.errorMessage || 'Decoding failed');
        }
      } else if (mode === 'encode') {
        try {
          const parsedPayload = JSON.parse(payload);

          const config = JwtTokenService.createConfiguration('encode', {
            algorithm: toSupportedAlgorithm(algorithm),
            secret,
            payload: parsedPayload,
            expiresIn,
            issuer: issuer || undefined,
            audience: audience || undefined
          });

          result = await JwtTokenService.encode(config);

          if (result.status === 'success') {
            setOutput(result.outputData as string);

            // Show header and payload for generated token
            if (result.metadata) {
              const generatedHeader = {
                alg: config.algorithm,
                typ: 'JWT'
              };
              const generatedPayload = JSON.parse(payload);

              setHeader(JSON.stringify(generatedHeader, null, 2));
              setPayload(JSON.stringify(generatedPayload, null, 2));
            }
          } else {
            setError(result.errorMessage || 'Encoding failed');
          }
        } catch (parseError) {
          setError('Invalid JSON in payload field');
        }
      } else if (mode === 'verify') {
        if (!secret.trim()) {
          setError('Secret key is required for verification');
          setIsLoading(false);
          return;
        }

        // Detect if the input is a public key or private key for RS algorithms
        const isRSAlgorithm = algorithm && algorithm.startsWith('RS');
        const keyConfig = isRSAlgorithm ? detectKeyType(secret) : { secret };

        const verifyConfig = JwtTokenService.createConfiguration('verify', {
          token: input,
          secret,
          ...keyConfig,
          algorithm: toSupportedAlgorithm(algorithm)
        });
        result = await JwtTokenService.verify(verifyConfig);

        if (result.status === 'success') {
          // Parse outputData if it's a string (it comes as JSON string from the service)
          const verificationData = typeof result.outputData === 'string'
            ? JSON.parse(result.outputData)
            : result.outputData;

          // Format the verification result in a user-friendly way
          const formattedOutput = JSON.stringify(verificationData, null, 2);
          setOutput(formattedOutput);

          if (result.metadata) {
            setHeader(JSON.stringify(result.metadata.header, null, 2));
            setPayload(JSON.stringify(result.metadata.payload, null, 2));
          }
        } else {
          setError(result.errorMessage || 'Verification failed');
        }
      }

      // Check if this is still the latest operation
      if (processingRef.current === processingKey) {
        // Only clear error if operation was successful
        if (result && result.status === 'success') {
          setError(null);
        }
      }
    } catch (err) {
      if (processingRef.current === processingKey) {
        const errorMessage = ErrorHandler.handle(err, EncoderToolType.JWT_TOKEN);
        setError(errorMessage);
        setOutput('');
        setHeader('');
        setPayload('');
      }
    } finally {
      if (processingRef.current === processingKey) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (input && mode === 'decode') {
      const timer = setTimeout(() => {
        handleProcess();
      }, 300); // Debounce for real-time processing

      return () => clearTimeout(timer);
    } else if (input && mode === 'verify' && secret.trim()) {
      const timer = setTimeout(() => {
        handleProcess();
      }, 300); // Debounce for real-time processing

      return () => clearTimeout(timer);
    } else if (mode === 'encode' && payload && secret) {
      const timer = setTimeout(() => {
        handleProcess();
      }, 500); // Slightly longer debounce for encoding

      return () => clearTimeout(timer);
    } else if (mode !== 'encode' && !input) {
      // Only clear output when not in encode mode and there's no input
      setOutput('');
      setError(null);
      setHeader('');
      setPayload('');
      setIsLoading(false);
    }
  }, [input, mode, payload, secret, algorithm]); // Add algorithm to dependencies to prevent state inconsistencies

  const handleCopy = async (text: string) => {
    if (!text) return;

    try {
      const success = await ClipboardUtils.copyText(text);
      if (success) {
        setCopyFeedback('copied');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      } else {
        setCopyFeedback('error');
        setTimeout(() => setCopyFeedback('idle'), 2000);
      }
    } catch (error) {
      console.error('Copy error:', error);
      setCopyFeedback('error');
      setTimeout(() => setCopyFeedback('idle'), 2000);
    }
  };

  const getButtonText = () => {
    switch (copyFeedback) {
      case 'copied':
        return 'Copied!';
      case 'error':
        return 'Failed!';
      default:
        return 'Copy Result';
    }
  };

  const getButtonClass = () => {
    const baseClass = 'copy-button';
    switch (copyFeedback) {
      case 'copied':
        return `${baseClass} copy-copied`;
      case 'error':
        return `${baseClass} copy-error`;
      default:
        return `${baseClass} copy-idle`;
    }
  };

  const clearAll = () => {
    setInput('');
    setOutput('');
    setHeader('');
    setPayload('');
    setSecret('');
    setError(null);
    setCopyFeedback('idle');
    setShowSecretWarning(false);
  };

  const generateSamplePayload = () => {
    const samplePayload = {
      sub: '1234567890',
      name: 'John Doe',
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + 3600,
      role: 'user',
      permissions: ['read', 'write']
    };
    setPayload(JSON.stringify(samplePayload, null, 2));
  };

  const formatJson = (text: string) => {
    try {
      const parsed = JSON.parse(text);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return text;
    }
  };

  return (
    <div className="jwt-token-encoder">
      <div style={{ marginBottom: '2rem' }}>
        <h1>JWT Token Encoder/Decoder</h1>
        <p>
          Decode JWT tokens to inspect contents, verify signatures, or generate new tokens for testing
        </p>
      </div>

      <div className="security-notice">
        <div className="notice-icon">🔒</div>
        <div className="notice-content">
          <strong>Security Notice:</strong> All JWT operations are performed client-side in your browser.
          Never enter production secrets or sensitive tokens in online tools.
        </div>
      </div>

      <div className="encoder-container">
        <div className="form-group">
          <label htmlFor="mode">Operation Mode</label>
          <select
            id="mode"
            value={mode}
            onChange={(e) => setMode(e.target.value as 'decode' | 'encode' | 'verify')}
            data-testid="mode-select"
          >
            <option value="decode">Decode Token</option>
            <option value="encode">Generate Token</option>
            <option value="verify">Verify Token</option>
          </select>
        </div>

        {mode !== 'decode' && (
          <div className="form-group">
            <label htmlFor="algorithm">Algorithm</label>
            <select
              id="algorithm"
              value={algorithm}
              onChange={(e) => setAlgorithm(e.target.value as JwtAlgorithm)}
              data-testid="algorithm-select"
            >
              <option value={JwtAlgorithm.HS256}>HS256 (HMAC-SHA256)</option>
              <option value={JwtAlgorithm.HS384}>HS384 (HMAC-SHA384)</option>
              <option value={JwtAlgorithm.HS512}>HS512 (HMAC-SHA512)</option>
              <option value={JwtAlgorithm.RS256}>RS256 (RSA-SHA256)</option>
              <option value={JwtAlgorithm.RS384}>RS384 (RSA-SHA384)</option>
              <option value={JwtAlgorithm.RS512}>RS512 (RSA-SHA512)</option>
              <option value={JwtAlgorithm.ES256}>ES256 (ECDSA-SHA256)</option>
              <option value={JwtAlgorithm.ES384}>ES384 (ECDSA-SHA384)</option>
              <option value={JwtAlgorithm.ES512}>ES512 (ECDSA-SHA512)</option>
            </select>
          </div>
        )}

        {(mode === 'encode' || mode === 'verify') && (
          <div className="form-group">
            <label htmlFor="secret">
              {mode === 'encode' && algorithm && algorithm.startsWith('RS')
                ? 'Private Key (Any format)'
                : mode === 'verify' && algorithm && algorithm.startsWith('RS')
                ? 'Private Key or Public Key (Any format)'
                : 'Secret Key'}
              <button
                type="button"
                className="info-button"
                onClick={() => setShowSecretWarning(!showSecretWarning)}
                data-testid="secret-info-button"
              >
                ℹ️
              </button>
            </label>
            <textarea
              id="secret"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={
                mode === 'encode' && algorithm && algorithm.startsWith('RS')
                  ? `Paste your RSA private key here (PEM format or any text)\n-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----\n\nOr simply enter any secret text`
                  : mode === 'verify' && algorithm && algorithm.startsWith('RS')
                  ? `Paste your RSA private key or public key here (PEM format or any text)\n-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----\n\nOr\n-----BEGIN PUBLIC KEY-----\n...\n-----END PUBLIC KEY-----`
                  : mode === 'encode'
                  ? 'Enter secret key for signing (e.g., your-256-bit-secret)'
                  : 'Enter secret key for verification'
              }
              className="secret-input"
              data-testid="secret-input"
              rows={(mode === 'encode' || mode === 'verify') && algorithm && algorithm.startsWith('RS') ? 6 : 1}
            />
            {showSecretWarning && (
              <div className="secret-warning" data-testid="secret-warning">
                <strong>⚠️ Important:</strong> {mode === 'encode' && algorithm && algorithm.startsWith('RS')
                  ? 'RSA private keys are visible on this page. This tool generates deterministic keys from your input for development purposes.'
                  : mode === 'verify' && algorithm && algorithm.startsWith('RS')
                  ? 'RSA keys are visible on this page. This tool supports both private and public keys for verification and generates deterministic keys from your input for development purposes.'
                  : 'Secret keys are visible on this page.'}
                {' '}Use this tool only with test tokens and secrets. Never use production credentials.
              </div>
            )}
          </div>
        )}

        {mode === 'encode' && (
          <>
            <div className="form-group">
              <label htmlFor="payload">JWT Payload (JSON)</label>
              <div className="payload-controls">
                <textarea
                  id="payload"
                  value={payload}
                  onChange={(e) => setPayload(formatJson(e.target.value))}
                  placeholder={"{\n  \"sub\": \"1234567890\",\n  \"name\": \"John Doe\",\n  \"iat\": 1516239022\n}"}
                  className="payload-textarea"
                  rows={8}
                  data-testid="payload-input"
                />
                <button
                  type="button"
                  onClick={generateSamplePayload}
                  className="sample-button"
                  data-testid="sample-payload-button"
                >
                  Generate Sample Payload
                </button>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="expires-in">Expires In</label>
                <input
                  type="text"
                  id="expires-in"
                  value={expiresIn}
                  onChange={(e) => setExpiresIn(e.target.value)}
                  placeholder="1h (1 hour, 30m, 7d, etc.)"
                  data-testid="expires-in-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="issuer">Issuer (Optional)</label>
                <input
                  type="text"
                  id="issuer"
                  value={issuer}
                  onChange={(e) => setIssuer(e.target.value)}
                  placeholder="your-app.com"
                  data-testid="issuer-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="audience">Audience (Optional)</label>
                <input
                  type="text"
                  id="audience"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder="api.your-app.com"
                  data-testid="audience-input"
                />
              </div>
            </div>
          </>
        )}

        {mode !== 'encode' && (
          <div className="form-group">
            <label htmlFor="input-token">
              {mode === 'decode' ? 'JWT Token to Decode' : 'JWT Token to Verify'}
            </label>
            <textarea
              id="input-token"
              value={input}
              onChange={(e) => setInput(e.target.value.trim())}
              placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c"
              className="token-textarea"
              rows={4}
              data-testid="input-token"
            />
          </div>
        )}

        {error && (
          <div className="error-message" data-testid="error-message">
            {error}
          </div>
        )}

        {isLoading && (
          <div className="loading-indicator" data-testid="loading-indicator">
            Processing JWT token...
          </div>
        )}

        {(header || payload) && (
          <div className="token-parts">
            <div className="token-section">
              <h3>Header</h3>
              <textarea
                value={header}
                readOnly
                className="json-output"
                rows={6}
                data-testid="header-output"
              />
              <button
                onClick={() => handleCopy(header)}
                className="copy-part-button"
                data-testid="copy-header-button"
              >
                Copy Header
              </button>
            </div>

            <div className="token-section">
              <h3>Payload</h3>
              <textarea
                value={payload}
                readOnly
                className="json-output"
                rows={8}
                data-testid="payload-output"
              />
              <button
                onClick={() => handleCopy(payload)}
                className="copy-part-button"
                data-testid="copy-payload-button"
              >
                Copy Payload
              </button>
            </div>
          </div>
        )}

        {output && mode !== 'decode' && (
          <div className="output-section">
            <h3>{mode === 'encode' ? 'Generated JWT Token' : 'Verification Result'}</h3>
            <textarea
              value={output}
              readOnly
              className="output-textarea"
              rows={mode === 'encode' ? 4 : 10}
              data-testid="output-result"
            />
            <div className="button-group">
              <button
                ref={copyButtonRef}
                onClick={() => handleCopy(output)}
                className={getButtonClass()}
                data-testid="copy-result-button"
              >
                {getButtonText()}
              </button>
              <button
                onClick={clearAll}
                className="clear-button"
                data-testid="clear-button"
              >
                Clear All
              </button>
            </div>
          </div>
        )}

        <div className="info-section">
          <h3>JWT Reference</h3>
          <div className="reference-grid">
            <div className="reference-item">
              <strong>Structure:</strong> header.payload.signature
            </div>
            <div className="reference-item">
              <strong>Header:</strong> Algorithm and token type
            </div>
            <div className="reference-item">
              <strong>Payload:</strong> Claims (user data, permissions, etc.)
            </div>
            <div className="reference-item">
              <strong>Signature:</strong> Cryptographic signature
            </div>
            <div className="reference-item">
              <strong>Common Claims:</strong> sub, iss, aud, exp, iat, nbf
            </div>
            <div className="reference-item">
              <strong>Algorithms:</strong> HS*, RS*, ES* for signing
            </div>
          </div>

          <div className="security-tips">
            <h4>🔒 Security Best Practices</h4>
            <ul>
              <li>Use strong, random secrets (minimum 256 bits for HS256)</li>
              <li>Set appropriate expiration times</li>
              <li>Include issuer and audience claims when possible</li>
              <li>Validate all claims on the server side</li>
              <li>Use asymmetric algorithms (RS*/ES*) in production</li>
              <li>Never include sensitive data in JWT payload</li>
              <li>Use HTTPS for all JWT transmissions</li>
            </ul>
          </div>
        </div>
      </div>

      {/* No-JS Fallback */}
      <noscript>
        <div className="fallback-form">
          <h3>JavaScript Required</h3>
          <p>JWT token processing requires JavaScript. Please enable JavaScript to use this tool.</p>
          <p>For command-line JWT processing, consider using:</p>
          <pre><code># Decode JWT
jwt decode "your.jwt.token"

# Generate JWT
jwt sign --alg HS256 --secret "your-secret" payload.json</code></pre>
        </div>
      </noscript>
    </div>
  );
};